import { isIgnoredErrorMessage } from 'rollbarConfig';
import { refreshToken } from './auth';

/*
 * True when the request path has a segment that is empty or the literal string "undefined"/"null"
 * — the tell-tale sign of an id/param that was missing when the URL was built
 * (e.g. `/api/contracts/${id}` with id === undefined → `/api/contracts/undefined`). Such requests
 * can only 404, so we skip them rather than generate console + Rollbar noise.
 */
const hasMissingPathParam = (input: URL | RequestInfo): boolean => {
  let pathname: string;
  try {
    const url =
      input instanceof URL
        ? input
        : new URL(input instanceof Request ? input.url : String(input), window.location.origin);
    pathname = url.pathname;
  } catch {
    return false;
  }
  return pathname
    .split('/')
    .slice(1) // drop the empty segment before the leading slash
    .some(segment => segment === '' || segment === 'undefined' || segment === 'null');
};

/*
 * adds auth token to request header
 * refreshes token and retries once if response status is 401 or 403
 * returns response.json() on success
 * returns default_T if the response has no content or the request fails
 */
const fetchWithRetry = async <T>(input: URL | RequestInfo, init: RequestInit, default_T: T): Promise<T> => {
  // Never issue a request for a resource whose id/param is missing — it can only 404.
  if (hasMissingPathParam(input)) return default_T;

  const logFailure = (error: unknown) => {
    // Cancellation of an obsolete request is expected, not a server or authentication error.
    if (init.signal?.aborted && error instanceof Error && error.name === 'AbortError') return;
    console.error(error);
  };

  try {
    let token = localStorage.getItem('token');
    if (!token) throw new Error('Unauthenticated');

    init.headers = new Headers(init.headers);
    init.headers.set('Authorization', `Bearer ${token}`);

    let response = await fetch(input, init);

    // Only an expired/invalid token is worth retrying: refresh and re-issue the request once.
    if (response.status === 401 || response.status === 403) {
      token = await refreshToken();
      if (!token) throw new Error('Unauthenticated');
      init.headers.set('Authorization', `Bearer ${token}`);
      response = await fetch(input, init);
    }

    if (response.ok)
      return response.json().catch(e => {
        logFailure(e);
        return default_T;
      });

    // A 404 means "no such resource" — callers pass an empty default for this, so it is not an
    // error worth logging. Surface every other non-OK status (incl. 5xx and surviving auth failures).
    if (response.status !== 404) {
      const text = await response.text();
      // Known-benign backend messages are logged at info level for now to keep the console quiet
      // while they are handled gracefully upstream; genuine errors stay loud. See the
      // ignoredMessages TODO in rollbarConfig.ts.
      const log = isIgnoredErrorMessage(text) ? console.info : console.error;
      log(`Error ${response.status}: ${text}`);
    }
  } catch (e) {
    logFailure(e);
  }
  return default_T;
};

export default fetchWithRetry;
